import type {ReactElement} from 'react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {
  DisplayType,
  ProductType,
  ThumbNailType,
  Status,
  PublishingAnalyticsAction,
  type PosterProductDataForProcessDesign,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {PublishingOptionListItem} from '@Panels/poster-publishing-options-panel/components/publishing-option-list-item/publishing-option-list-item';
import {useDispatch} from 'react-redux';
import styles from './publishing-digital-signage-list.module.scss';
import {selectionTypeSubDisplay, setOrderDetails, setProductType, subPanelFlow, setShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import {ModalDetails} from '@Components/modal/modal-context';
import type {PublishingOptions} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {PublishingProgressBar} from '@Panels/poster-publishing-options-panel/components/publishing-progress-bar/publishing-progress-bar';
import {useHashedId, usePosterProduct, useShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import type {PublishingDigitalSignageListProps} from './publishing-digital-signage-list.types';
import {getDefaultOrderDetails, getProductUsed} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {GA4EventParamName} from '@Libraries/ga-events';
import {processDesignForPublishing} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';

export function PublishingDigitalSignageList({displayType, setTopShadow}: PublishingDigitalSignageListProps): ReactElement<any> {
  const dispatch = useDispatch();
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const isFirstRender = useRef(true);
  const signageContentRef = useRef<HTMLDivElement>(null);
  const [designProcessClicked, setDesignProcessClicked] = useState(false);
  const premiumProductDataForPoster = usePosterProduct();
  const hashedId = useHashedId();
  const showLoader = useShowLoader();
  const {topShadow} = useTopBottomScrollShadow(signageContentRef);

  useEffect(() => {
    setTopShadow(topShadow);
  }, [topShadow]);

  const productUsed = getProductUsed();
  const status = productUsed?.status ?? '';
  const isRegenarationDueToDeletionNeed = productUsed?.needsRegenerationDueToDeletion ?? false;

  let productPrice = '';
  if (productUsed?.needsPurchase) {
    productPrice = productUsed?.price ?? '';
  }

  const isOrderOutdated = productUsed?.status === Status.OUTDATED;

  useEffect(() => {
    if (isFirstRender.current) {
      dispatch(setProductType(premiumProductDataForPoster ? ProductType.POSTER : ProductType.PRINT));
      dispatch(setOrderDetails(getDefaultOrderDetails()));
      isFirstRender.current = false;
    }
  }, []);

  const savePosterPublishingInfoInServer = (): void => {
    void window.PMW.writeLocal('poster/savePublishingInfo', {
      hid: hashedId,
    });
  };

  const processDesign = (): void => {
    if (productUsed?.status !== Status.DOWNLOADABLE || productUsed?.needsRegenerationDueToDeletion) {
      dispatch(setShowLoader(true));
    }
    processDesignForPublishing(productUsed ?? ({} as PosterProductDataForProcessDesign));
    setDesignProcessClicked(true);
  };

  useEffect(() => {
    if (!showLoader && status === Status.DOWNLOADABLE && !isRegenarationDueToDeletionNeed && designProcessClicked) {
      dispatch(subPanelFlow());
    }
  }, [status, designProcessClicked, isRegenarationDueToDeletionNeed]);

  const publishingOptionsImages: PublishingOptions[] = [
    {
      id: SubFlowType.SMART_TV_SUBDISPLAY,
      iconName: 'icon-tv',
      heading: window.i18next.t('pmwjs_play_on_smart_tv'),
      thumbNailType: ThumbNailType.ICON,
      subFlowType: SubFlowType.SMART_TV_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'PlayOnSmartTV'});
      },
    },
    {
      id: SubFlowType.RAYDIANT_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_raydiant'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-displai.png',
      subFlowType: SubFlowType.RAYDIANT_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Displai'});
      },
    },
    {
      id: SubFlowType.SCREEN_CLOUD_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_screencloud'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-screencloud.png',
      subFlowType: SubFlowType.SCREEN_CLOUD_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'ScreenCloud'});
      },
    },
    {
      id: SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_signagelive_widget'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-signagelive.png',
      subFlowType: SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'SignageLive'});
      },
    },
    {
      id: SubFlowType.POSTERBOOKING_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_posterbooking'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-poster-booking.png',
      subFlowType: SubFlowType.POSTERBOOKING_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'PosterBooking'});
      },
    },
    {
      id: SubFlowType.FUGO_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_fugo'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-fugo.png',
      subFlowType: SubFlowType.FUGO_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Fugo'});
      },
    },
    {
      id: SubFlowType.YODECK_SUBDISPLAY,
      iconName: 'icon-share-external',
      heading: window.i18next.t('pmwjs_yodeck'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-yodeck.png',
      subFlowType: SubFlowType.YODECK_SUBDISPLAY,
      price: productPrice,
      gaTracking: (): void => {
        window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Yodeck'});
      },
    },
    /**
     * 6th December 2024 - Disabling VIZROM since their site is down (indefinitely)
     */
    // {
    //   id: SubFlowType.VIZROM_SUBDISPLAY,
    //   iconName: 'icon-share-external',
    //   heading: window.i18next.t('pmwjs_vizrom'),
    //   thumbNailType: ThumbNailType.IMAGE,
    //   imgSrc: 'publishing-vizrom.png',
    //   subFlowType: SubFlowType.VIZROM_SUBDISPLAY,
    //   price: productPrice,
    // gaTracking: (): void => {
    // window.PMW.gtm.trackGA4CustomEvent(AnalyticsAction.SIGNAGE, {[GA4EventParamName.TYPE]: 'Vizrom'});
    // },
    // },
    /**
     * 29th September 2025 - Dripboards option is disabled due to an internal issue at their end
     */
    // {
    //   id:  SubFlowType.DRIPBOARDS_SUBDISPLAY,
    //   iconName: 'icon-share-external',
    //   heading: window.i18next.t('pmwjs_dripboards'),
    //   thumbNailType: ThumbNailType.IMAGE,
    //   imgSrc: 'publishing-dripboards.png',
    //   subFlowType: SubFlowType.DRIPBOARDS_SUBDISPLAY,
    //   price: productPrice,
    // },
  ];

  let panelStyle = '';
  if (inMobile) {
    panelStyle = `spacing-p-3 ${styles.publishingOptionsGapPanelMobile}`;
  } else {
    panelStyle = displayType === DisplayType.MODAL ? styles.publishingOptionsGapImagePanel : `flex-v-row spacing-p-3 ${styles.publishingOptionsGapImageDropDown}`;
  }

  const onSignageItemClick = (option: PublishingOptions): void => {
    if (option.gaTracking) {
      option.gaTracking();
    }
    savePosterPublishingInfoInServer();
    dispatch(selectionTypeSubDisplay(option.subFlowType ?? SubFlowType.DEFAULT));
    processDesign();
  };

  const getPanelContent = (): ReactElement<any> => {
    return (
      <div ref={signageContentRef} className={`animated fadeIn ${styles.signageDropDownHeight}`}>
        <div className={panelStyle}>
          {publishingOptionsImages.map((option: PublishingOptions) => {
            return (
              <PublishingOptionListItem
                key={option.id}
                iconName={option.iconName}
                heading={option.heading}
                thumbNailType={option.thumbNailType}
                imgSrc={option.imgSrc}
                showTwoButtons={option.subFlowType === SubFlowType.SMART_TV_SUBDISPLAY && isOrderOutdated}
                customThumbnailClasses="bg-neutral-3 content-body"
                price={option.price}
                onClick={() => {
                  onSignageItemClick(option);
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return showLoader ? <PublishingProgressBar /> : getPanelContent();
}
